import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Bookmark,
  BookmarkPlus,
  CalendarDays,
  Check,
  CircleUserRound,
  Columns3,
  Rows3,
  GitPullRequestArrow,
  Inbox,
  Layers3,
  Pencil,
  Trash2,
  Users,
  X,
} from "lucide-react";

import { AuthContext } from "src/context/AuthContext";
import { WsDropdown, WsSegmented } from "src/stage/WsControls";
import { getPipelines, getSavedViews, saveSavedViews } from "../../CachedData";
import { DATE_PRESETS, DEFAULT_FILTERS } from "./constants";

const DEFAULT_VIEWS = [
  { id: "all", name: "All leads", icon: Users },
  { id: "today", name: "Today’s leads", icon: CalendarDays },
  { id: "unassigned", name: "Unassigned", icon: Inbox },
  { id: "mine", name: "My leads", icon: CircleUserRound },
  { id: "moved", name: "Moved today", icon: GitPullRequestArrow },
];

function freshFilters() {
  return structuredClone(DEFAULT_FILTERS);
}

export function getDefaultViewFilters(viewId, userId) {
  const baseFilters = freshFilters();

  switch (viewId) {
    case "today":
      return { ...baseFilters, createdDate: { preset: DATE_PRESETS.TODAY } };
    case "unassigned":
      return { ...baseFilters, assignedTo: ["unassigned"] };
    case "mine":
      return { ...baseFilters, assignedTo: userId ? [userId] : [] };
    case "moved":
      return { ...baseFilters, stageUpdated: { preset: DATE_PRESETS.TODAY } };
    default:
      return baseFilters;
  }
}

function isSameFilters(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

export default function LeadViews({
  viewCounts = {},
  workspaceId,
  filters,
  setFilters,
  activeFilterCount,
  pipelineId,
  onPipelineChange,
  onViewSelected,
  layout,
  onLayoutChange,
}) {
  const { currentUser } = useContext(AuthContext);
  const currentUserId = currentUser?.id || null;
  const [pipelines, setPipelines] = useState([]);
  const [pipelinesLoading, setPipelinesLoading] = useState(true);
  const [savedViews, setSavedViews] = useState(getSavedViews);
  const [isCreatingView, setIsCreatingView] = useState(false);
  const [isManagingViews, setIsManagingViews] = useState(false);
  const [viewName, setViewName] = useState("");
  const [editingViewId, setEditingViewId] = useState(null);

  useEffect(() => {
    let active = true;

    getPipelines(workspaceId)
      .then((response) => {
        if (!active) return;
        const nextPipelines = Array.isArray(response) ? response : response?.data || [];
        setPipelines(nextPipelines);

        if (!pipelineId && nextPipelines.length) {
          const defaultPipeline = nextPipelines.find((pipeline) => pipeline.is_default) || nextPipelines[0];
          onPipelineChange?.(defaultPipeline.id);
        }
      })
      .catch(() => {
        if (active) setPipelines([]);
      })
      .finally(() => {
        if (active) setPipelinesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [onPipelineChange, pipelineId, workspaceId]);

  useEffect(() => {
    saveSavedViews(savedViews);
  }, [savedViews]);

  useEffect(() => {
    const handleSavedViewsReset = () => {
      setSavedViews(getSavedViews());
      setFilters(getDefaultViewFilters("all", currentUserId));
    };

    window.addEventListener("saved-views-reset", handleSavedViewsReset);
    return () => window.removeEventListener("saved-views-reset", handleSavedViewsReset);
  }, [currentUserId, setFilters]);

  const defaultViews = useMemo(
    () => DEFAULT_VIEWS.map((view) => ({
      ...view,
      filters: getDefaultViewFilters(view.id, currentUserId),
      count: viewCounts[view.id] || 0,
    })),
    [currentUserId, viewCounts]
  );

  const activeDefaultView = useMemo(
    () => defaultViews.find((view) => isSameFilters(filters, view.filters)),
    [defaultViews, filters]
  );

  const activeSavedView = useMemo(
    () => savedViews.find((view) => isSameFilters(filters, view.filters)),
    [filters, savedViews]
  );

  const selectFilters = useCallback((nextFilters) => {
    setFilters(structuredClone(nextFilters));
    onViewSelected?.();
  }, [onViewSelected, setFilters]);

  const saveView = useCallback(() => {
    const name = viewName.trim();
    if (!name || activeFilterCount === 0) return;

    const duplicate = savedViews.some((view) => view.name.toLowerCase() === name.toLowerCase());
    if (duplicate) {
      window.alert("A view with this name already exists.");
      return;
    }

    setSavedViews((previous) => [...previous, {
      id: Date.now().toString(),
      name,
      filters: structuredClone(filters),
    }]);
    setViewName("");
    setIsCreatingView(false);
  }, [activeFilterCount, filters, savedViews, viewName]);

  const renameView = useCallback((viewId, nextName) => {
    const name = nextName.trim();
    if (!name) {
      setEditingViewId(null);
      return;
    }

    setSavedViews((previous) => previous.map((view) => (
      view.id === viewId ? { ...view, name } : view
    )));
    setEditingViewId(null);
  }, []);

  const pipelineOptions = pipelines.map((pipeline) => ({ value: String(pipeline.id), label: pipeline.name }));
  const savedOptions = [{ value: "", label: "None" }, ...savedViews.map((view) => ({ value: view.id, label: view.name }))];

  return (
    <section className="crm-lead-views" aria-label="Lead work queue">
      <div className="ws-controls crm-view-bar">
        <div className="crm-pipeline-picker">
          {pipelinesLoading || pipelineOptions.length === 0 ? (
            <span className="ws-dd crm-dd-static" aria-label="Current pipeline"><span className="ws-dd-tile" aria-hidden="true"><Layers3 size={15} strokeWidth={2.1} /></span><span className="ws-dd-value">{pipelinesLoading ? "Loading pipelines…" : "No pipeline available"}</span></span>
          ) : (
            <WsDropdown label="Pipeline" icon={Layers3} value={String(pipelineId || "")} options={pipelineOptions} onChange={(value) => onPipelineChange?.(value)} />
          )}
        </div>

        {onLayoutChange && (
          <WsSegmented
            label="Show leads as"
            value={layout}
            onChange={onLayoutChange}
            items={[{ id: "board", label: "Board", icon: Columns3 }, { id: "list", label: "List", icon: Rows3 }]}
          />
        )}

        <div className="crm-view-tabs" aria-label="Quick lead views">
          <WsSegmented
            label="Quick lead views"
            value={activeDefaultView?.id || ""}
            onChange={(id) => { const view = defaultViews.find((item) => item.id === id); if (view) selectFilters(view.filters); }}
            items={defaultViews.map((view) => ({ id: view.id, label: view.name, icon: view.icon, count: view.count }))}
          />
        </div>

        <div className="crm-saved-views">
          {savedViews.length > 0 && (
            <WsDropdown label="Saved view" icon={Bookmark} align="right" value={activeSavedView?.id || ""} options={savedOptions}
              onChange={(value) => { const view = savedViews.find((savedView) => savedView.id === value); if (view) selectFilters(view.filters); }} />
          )}
          <button
            type="button"
            onClick={() => setIsCreatingView(true)}
            disabled={activeFilterCount === 0}
            aria-label="Save current view"
            title={activeFilterCount === 0 ? "Apply filters before saving a view" : "Save the current filters"}
            className="ws-sort"
          >
            <BookmarkPlus size={15} className="ws-sort-icon" />
            <span>Save view</span>
          </button>
          {savedViews.length > 0 && (
            <button
              type="button"
              onClick={() => setIsManagingViews((value) => !value)}
              aria-pressed={isManagingViews}
              className="crm-icon-btn crm-icon-btn--sm"
              title="Manage saved views"
              aria-label="Manage saved views"
            >
              {isManagingViews ? <Check size={15} /> : <Pencil size={14} />}
            </button>
          )}
        </div>
      </div>

      {isCreatingView && (
        <div className="crm-view-panel ws-rise">
          <input
            autoFocus
            value={viewName}
            onChange={(event) => setViewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") saveView();
              if (event.key === "Escape") {
                setIsCreatingView(false);
                setViewName("");
              }
            }}
            placeholder="Name this working view"
            aria-label="View name"
            className="ws-input crm-view-name"
          />
          <button type="button" onClick={saveView} className="ws-btn ws-btn--primary ws-btn--sm">Save view</button>
          <button type="button" onClick={() => { setIsCreatingView(false); setViewName(""); }} className="crm-icon-btn crm-icon-btn--sm" aria-label="Cancel saving view">
            <X size={14} />
          </button>
        </div>
      )}

      {isManagingViews && (
        <div className="crm-view-panel ws-rise">
          {savedViews.map((view) => (
            <div key={view.id} className="crm-saved-chip">
              {editingViewId === view.id ? (
                <input
                  autoFocus
                  defaultValue={view.name}
                  aria-label={`Rename ${view.name}`}
                  onBlur={(event) => renameView(view.id, event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") renameView(view.id, event.currentTarget.value);
                    if (event.key === "Escape") setEditingViewId(null);
                  }}
                />
              ) : (
                <button type="button" onClick={() => selectFilters(view.filters)} className="crm-saved-chip-name">{view.name}</button>
              )}
              <button type="button" onClick={() => setEditingViewId(view.id)} aria-label={`Rename ${view.name}`}><Pencil size={13} /></button>
              <button type="button" className="is-danger" onClick={() => setSavedViews((previous) => previous.filter((item) => item.id !== view.id))} aria-label={`Delete ${view.name}`}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
